// CSP nonce setup - MUST be imported first before styled-components
import './cspNonceSetup';
import { applyAutoLoginBypass, runSsoAutoLoginGate } from '@common/auth/ssoAutoLogin';
import { resolveSsoAttempt, setSsoAutoLoginDisabled } from '@common/auth/ssoAutoLogin/state';
import { FeatureFlagsProvider } from '@common/feature-flags/FeatureFlags';
import {
  isAutomatedBrowser,
  isMonacoLoaderCancelation,
  listenForNetwork,
  normalizeChunkUrl,
  sentryIgnoreErrors,
  titleUntitledRejection
} from '@common/sentry';
import {
  getSqlIDEUrl,
  isAuthRoute,
  isEmbeddedSingUpUrl,
  isPublicURL,
  isSignInRoute,
  Private_Routes
} from '@common/url';
import { getBasePathFromUrl } from '@common/urlBasePath';
import { event as fullStoryEvent, getCurrentSessionURL as fullStoryGetCurrentSessionURL } from '@fullstory/browser';
import { ObjectLiteral } from '@prophecy/interfaces/generic';
import { isNodeEvnProduction } from '@prophecy/utils/env';
import { applyExpensiveCssOverrides, getIsExpensiveCssDisabled } from '@prophecy/utils/expensiveCss';
import { history, historyReplace, navigateAndRefresh, setBasePath } from '@prophecy/utils/history';
import { fullStoryIntegration } from '@sentry/fullstory';
import { init as sentryInit, reactRouterV6BrowserTracingIntegration } from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { StyleSheetManager } from 'styled-components';

import { AppMetadataProvider } from './context/appMetadata';
import { getAppMetadata } from './context/getAppMetadata';
import { UNSECURED_SSO_AUTO_LOGIN_DISABLED_KEY } from './context/globalConfig/constants';
import { MetricsProvider } from './context/metrics/context';
import { MixPanelProvider } from './context/mixpanel/context';
import { AppMetadataProviderType, UserDetails } from './context/types';
import { finishPendingSsoLogout } from './data/apis/api';
import { fetchControlCentreAuth, fetchUsageMetrics, postUsageMetrics, updateTimeStamp } from './data/apis/athena';
import { ControlCenterResponse, UsgaeMetricsResponse } from './data/apis/types';
import { redirectToAuth } from './data/rest/redirectToAuth';
import { isShareUrl } from './data/rest/util';
import { makeGraphQlCallWithoutShareTokens } from './data/util';
import { DevTool } from './devtool';
import { MainApp } from './MainApp';
import { cloneSharedProjectMutation } from './pages/Sql/Replay/Share/queries';
import { getReplayId, isSharedReplay, getShareId, isSharedProject } from './pages/Sql/Replay/Share/shareUrlUtils';
import { getGraphQlEndPoint } from './utils/getServerUrl';
import { listenForPageUnload } from './utils/pageUnload';
import { isReplayMode } from './utils/replayToken';
import { isShareMode, shareToken } from './utils/shareToken';
import { GlobalWindow } from './utils/types';
import { isNewUser } from './utils/user';
import 'reactflow/dist/style.css';
import '@common/appVersion';

// LSP heartbeats and bare log notifications carry no payload but fire hundreds of times a minute,
// evicting the fetch trail from Sentry's 100-entry breadcrumb buffer before an error can capture it.
const LSP_NOISE_BREADCRUMB = /"(?:method|query)":"(?:ping|Ping|pong|window\/logMessage)"/;

type SentryEnvironment = 'production' | 'production-vertical-apps' | 'staging' | 'development';

// A host missing from here never initialises Sentry, which is what keeps self-hosted installs from
// reporting to us. SENTRY_FORCE_INIT is a build-time override that points an off-host build at the
// dev project, so a reporting change can be verified without shipping to a SaaS host.
const SENTRY_ENVIRONMENT_BY_HOST: Record<string, SentryEnvironment | undefined> = {
  'app.prophecy.io': 'production',
  'app.prophecy.ai': 'production-vertical-apps',
  'staging.cloud.prophecy.io': 'staging'
};

const PROD_SENTRY_DSN = 'https://80b67469d06c4f53a053992a41b4bdf8@o449849.ingest.sentry.io/5433542';

// Each environment reports to its own project, so one environment's volume and issues cannot bury
// another's. The two production hosts share one project and differ only in the name they report under.
const SENTRY_DSN_BY_ENVIRONMENT: Record<SentryEnvironment, string> = {
  production: PROD_SENTRY_DSN,
  'production-vertical-apps': PROD_SENTRY_DSN,
  staging: 'https://e404eec67373e55b45fcc3e63f6a37a0@o449849.ingest.us.sentry.io/4512095046336512',
  development: 'https://d50eed689679eeaac8e3a0c88672f70a@o449849.ingest.us.sentry.io/4511847002341376'
};

const isSentryHostOverridden = process.env.SENTRY_FORCE_INIT === 'true';

function getSentryConfig() {
  const environment: SentryEnvironment | undefined =
    SENTRY_ENVIRONMENT_BY_HOST[window.location.host] ?? (isSentryHostOverridden ? 'development' : undefined);

  if (!environment) return undefined;

  return {
    environment,
    dsn: SENTRY_DSN_BY_ENVIRONMENT[environment],
    // transactions arrive one per page load and route change against one error per actual failure,
    // so sampling them is what fits a smaller quota without losing failures
    tracesSampleRate: environment === 'production' || environment === 'production-vertical-apps' ? 1.0 : 0.1
  };
}

const sentryConfig = getSentryConfig();

if (isNodeEvnProduction() && sentryConfig && !isAutomatedBrowser()) {
  // store util to get fullstory session url on global window, so while testing folks can directly get it from console and forward
  (window as unknown as GlobalWindow).getFullStorySessionURL = fullStoryGetCurrentSessionURL;

  console.warn('Configuring Sentry');
  sentryInit({
    dsn: sentryConfig.dsn,
    ignoreErrors: sentryIgnoreErrors,
    environment: sentryConfig.environment,
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      fullStoryIntegration('prophecy', {
        client: { event: fullStoryEvent, getCurrentSessionURL: fullStoryGetCurrentSessionURL }
      })
    ],
    tracesSampleRate: sentryConfig.tracesSampleRate,
    beforeBreadcrumb(breadcrumb) {
      // filter out default graphql breadcrumb as we have custom breadcrumb for graphql
      if (breadcrumb.category === 'fetch' && getGraphQlEndPoint() === breadcrumb.data?.url) return null;
      if (breadcrumb.category?.startsWith('LSP') && LSP_NOISE_BREADCRUMB.test(breadcrumb.message ?? '')) return null;
      return breadcrumb;
    },
    beforeSend(event, hint) {
      // expected conditions opt out via ProphecyError.report; checked here so it also drops
      // unhandled rejections, which bypass the captureException wrapper
      if ((hint?.originalException as { report?: boolean })?.report === false) return null;
      if (isMonacoLoaderCancelation(hint?.originalException)) return null;
      return normalizeChunkUrl(titleUntitledRejection(event, hint));
    }
  });
  listenForNetwork();
  listenForPageUnload();
}

const returnMainApp = (data: AppMetadataProviderType, isLoggedIn: boolean) => {
  return (
    <StyleSheetManager>
      <FeatureFlagsProvider>
        <AppMetadataProvider value={data}>
          <MixPanelProvider>
            <MetricsProvider>
              <MainApp isLoggedIn={isLoggedIn} userId={data.user?.id as string} />
            </MetricsProvider>
          </MixPanelProvider>
        </AppMetadataProvider>
        <DevTool />
      </FeatureFlagsProvider>
    </StyleSheetManager>
  );
};

const updateUserMetrics = async () => {
  try {
    const metricsResponse = (await fetchUsageMetrics()) as UsgaeMetricsResponse;
    if (!metricsResponse?.data?.aggregateMetrics) {
      return;
    }
    const { data } = (await fetchControlCentreAuth()) as ControlCenterResponse;
    const { url, key } = data;
    if (!url || !key) {
      return;
    }

    const updateUsageMetricsResponse = (await postUsageMetrics(
      url,
      { aggregateMetrics: metricsResponse?.data?.aggregateMetrics },
      {
        Authorization: key
      }
    )) as ObjectLiteral;

    if (updateUsageMetricsResponse?.code === 200) {
      updateTimeStamp({ requestID: metricsResponse?.data?.requestID });
    }
  } catch (e) {
    console.error('error while updating usage metrics', e);
  }
};

// This function is used to clone the shared project when the user signup from the shared project
const handleAutoClone = async (isLoggedIn: boolean, user?: UserDetails) => {
  const searchParams = new URLSearchParams(window.location.search);
  const autoClone = searchParams.get('autoClone') === 'true';
  const projectId = shareToken.shareDetails?.projectUID;

  // If auto clone is enabled or the user is new, clone the shared project
  if ((autoClone || (user && isNewUser(user))) && isLoggedIn && projectId) {
    try {
      const {
        cloneSharedProject: { _id }
      } = await makeGraphQlCallWithoutShareTokens(cloneSharedProjectMutation, {
        uid: String(projectId),
        fabricUID: String(shareToken.shareDetails?.fabricUID ?? '')
      });
      const newUrl = getSqlIDEUrl({ uid: _id, onboarding: 'true', remix: 'true' });
      navigateAndRefresh(newUrl);
      return true;
    } catch (error) {
      console.error('Auto clone failed', error);
    }
  }
  return false;
};

const run = async () => {
  let pathname = window.location.pathname;

  // update the base path for the history api upfront, so it getBasePath points to correct product.
  setBasePath(getBasePathFromUrl(pathname));

  // Before any network call, so a hanging config endpoint cannot take the escape hatch down.
  const isAutoLoginBypassed = applyAutoLoginBypass(window.location.search);

  const searchParams = new URLSearchParams(window.location.search);
  const shareId = getShareId(pathname, searchParams);
  const replayId = getReplayId(pathname, searchParams);
  const data = await getAppMetadata(shareId, replayId);
  const isExpensiveCssDisabled = getIsExpensiveCssDisabled(data.globalConfig);
  applyExpensiveCssOverrides(isExpensiveCssDisabled);
  // if share-token/replay-token is present, consider user as logged in
  // for embed url consider user as non logged in
  const isLoggedIn = (Boolean(data?.user?.id) && !isEmbeddedSingUpUrl(pathname)) || isShareMode() || isReplayMode();

  // Set unconditionally: logout reads this too, and that only happens when the user IS logged in.
  setSsoAutoLoginDisabled(data.unsecuredConfig?.[UNSECURED_SSO_AUTO_LOGIN_DISABLED_KEY]);
  resolveSsoAttempt(isLoggedIn);

  if (await finishPendingSsoLogout(isLoggedIn)) return;

  if (data.shareUrl) {
    // Use history.replace so we don't prepend /metadata to vapp shareUrls
    if (!isSharedProject(data.shareUrl)) {
      history.replace(data.shareUrl);
    }
  }

  if (data.replayUrl) {
    // This condition also protects the changes in url path for replay
    if (!isSharedReplay(data.replayUrl)) {
      historyReplace(data.replayUrl);
    }
  }

  // check if we need to clone the shared project for user
  await handleAutoClone(isLoggedIn, data.user);

  pathname = window.location.pathname;

  // Signup and reset-password are auth routes the user chose deliberately — never hijack those.
  const isAutoLoginSurface = isSignInRoute(pathname) || !isAuthRoute(pathname);

  if (!isAutoLoginBypassed && !isLoggedIn && isAutoLoginSurface && !isPublicURL(pathname) && !isShareUrl()) {
    const isNavigatingToIdp = await runSsoAutoLoginGate();
    if (isNavigatingToIdp) return;
  }

  if (!isLoggedIn && !isAuthRoute(pathname) && !isPublicURL(pathname) && !isShareUrl()) {
    redirectToAuth(true);
  } else if (isLoggedIn && isAuthRoute(pathname)) {
    if (!shareToken.get()) {
      historyReplace(Private_Routes.Home.getUrlWithBasePath());
    }
  }

  if (isLoggedIn && data.globalConfig['prophecy.ui.usageMetrics.enabled']) {
    updateUserMetrics();
  }

  const rootElement = document.getElementById('root');
  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(returnMainApp(data, isLoggedIn));
  } else {
    throw new Error('Could not mount react app as element #root was not found');
  }
};

run();
